import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Platform, useColorScheme } from 'react-native';
import * as SecureStore from 'expo-secure-store';

import { palette, type ColorScheme, type ThemeColors } from '@bero/tokens';

type Override = ColorScheme | 'system';

const THEME_KEY = 'bero-theme';

function isOverride(v: unknown): v is Override {
  return v === 'light' || v === 'dark' || v === 'system';
}

/**
 * Web only: localStorage is synchronous, so this can run inside the
 * `useState` initializer — it executes during the very first render, before
 * anything paints, so there is no light->dark flash on reload.
 */
function readInitialWebOverride(): Override {
  if (Platform.OS !== 'web') return 'system';
  try {
    const raw = typeof localStorage === 'undefined' ? null : localStorage.getItem(THEME_KEY);
    return isOverride(raw) ? raw : 'system';
  } catch {
    return 'system'; // storage unavailable (privacy mode etc.) — fall back quietly
  }
}

function persistOverride(v: Override) {
  if (Platform.OS === 'web') {
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem(THEME_KEY, v);
    } catch {
      // storage unavailable — theme just won't persist this session
    }
    return;
  }
  SecureStore.setItemAsync(THEME_KEY, v).catch(() => {});
}

type ThemeCtx = {
  scheme: ColorScheme;
  colors: ThemeColors;
  toggle: () => void;
  setScheme: (s: Override) => void;
};

const Ctx = createContext<ThemeCtx | null>(null);

export function BeroThemeProvider({ children }: { children: React.ReactNode }) {
  const system = useColorScheme() ?? 'light';
  // Web: hydrated synchronously above, before first paint. Native: starts at
  // 'system' and is corrected below once expo-secure-store resolves (there is
  // no synchronous native storage API, so a same-session flash isn't
  // avoidable there — the native splash screen covers cold-start anyway).
  const [override, setOverrideState] = useState<Override>(readInitialWebOverride);

  useEffect(() => {
    if (Platform.OS === 'web') return;
    let cancelled = false;
    SecureStore.getItemAsync(THEME_KEY)
      .then((v) => { if (!cancelled && isOverride(v)) setOverrideState(v); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const setOverride = useCallback((v: Override) => {
    setOverrideState(v);
    persistOverride(v);
  }, []);

  const scheme: ColorScheme = override === 'system' ? (system as ColorScheme) : override;

  const toggle = useCallback(
    () => setOverride(scheme === 'dark' ? 'light' : 'dark'),
    [scheme, setOverride],
  );

  const value = useMemo(
    () => ({ scheme, colors: palette[scheme], toggle, setScheme: setOverride }),
    [scheme, toggle, setOverride],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTheme outside BeroThemeProvider');
  return ctx;
}
